// Reports API Service
import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import {
  MonthlySummaryResponse,
  ReportApiResponse
} from '../../models/ReportInterface';

/**
 * Get Monthly Summary Reports
 * GET /api/v1/reports/monthly-summary
 */
export async function getMonthlySummaryReports(): Promise<ReportApiResponse<MonthlySummaryResponse>> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/monthly-summary`,
  });
  
  return response;
}

/**
 * All saved month-end KPI snapshots (oldest → newest in list).
 * GET /api/v1/reports/monthly-summary/all
 */
export async function getAllMonthlyKpiSnapshots(): Promise<{ data?: MonthlySummaryResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/monthly-summary/all`,
  });
}

/**
 * Generate Monthly Summary Report
 * POST /api/v1/reports/monthly-summary/generate
 */
export async function generateMonthlySummaryReport(): Promise<ReportApiResponse<MonthlySummaryResponse>> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/monthly-summary/generate`,
    data: {},
  });
  
  return response;
}

/**
 * Get monthly summary data for dashboard
 */
export async function getMonthlySummaryData() {
  try {
    const response = await getMonthlySummaryReports();
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly summary data:', error);
    throw error;
  }
}

/**
 * Generate monthly summary data on demand
 */
export async function generateMonthlySummaryData() {
  try {
    const response = await generateMonthlySummaryReport();
    return response.data;
  } catch (error) {
    console.error('Error generating monthly summary data:', error);
    throw error;
  }
}

/** Row for revenue & cashflow by customer report */
export interface CustomerRevenueCashflowRow {
  borrowerId: string;
  borrowerName: string;
  customerId: string;
  interestCollected: number;
  lateFeesCollected: number;
  revenue: number;
  principalCollected: number;
  totalDisbursed: number;
  cashInflow: number;
  netCashflow: number;
}

export interface RevenueCashflowReportResponse {
  reportDate: string;
  totalCustomers: number;
  totalRevenue: number;
  totalNetCashflow: number;
  rows: CustomerRevenueCashflowRow[];
  message: string;
  statusCode: number;
}

/**
 * Get revenue and cashflow by customer report
 * GET /api/v1/reports/revenue-cashflow
 */
export async function getRevenueCashflowReport(): Promise<{ data?: RevenueCashflowReportResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/revenue-cashflow`,
  });
}

/**
 * Send revenue/cashflow report to email
 * POST /api/v1/reports/revenue-cashflow/send-email
 * @param email Optional; if not provided uses backend default report email
 */
export async function sendRevenueCashflowReportEmail(email?: string): Promise<{ data?: { message?: string }; message?: string }> {
  const params = email ? { email } : {};
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/revenue-cashflow/send-email`,
    params,
    data: {},
  });
}

/** Row: interest collected in current month (till today) by customer + expected interest + last payment date */
export interface InterestCollectedByCustomerRow {
  borrowerId: string;
  borrowerName: string;
  customerId: string;
  interestCollectedCurrentMonth: number;
  expectedInterest: number;
  lastPaymentDate: string | null; // ISO instant
}

export interface InterestCollectedByCustomerResponse {
  reportDate: string;
  totalCustomers: number;
  totalInterestCollectedCurrentMonth: number;
  totalExpectedInterest: number;
  rows: InterestCollectedByCustomerRow[];
  message: string;
  statusCode: number;
}

/**
 * Get interest collected in current month (till today) by each customer, with last payment date.
 * GET /api/v1/reports/interest-collected-current-month
 */
export async function getInterestCollectedByCustomerCurrentMonth(): Promise<{ data?: InterestCollectedByCustomerResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/interest-collected-current-month`,
  });
}
